// Core
import React, {
  createContext,
  useContext,
  useMemo,
} from 'react';
import { useMediaQuery } from 'react-responsive';

// Types
import { BreakpointSize } from '../../types/global';

// Config
import { BREAKPOINTS } from '../../config/screen';

// Hook
import { BreakpointContextValue, BreakpointProviderProps } from './useBreakpoint.types';

/* ************** HELPER FUNCTIONS ******************* */

const calculateCurrentBreakpoint = (
  isXs: boolean,
  isSm: boolean,
  isMd: boolean,
  isLg: boolean,
  isXl: boolean,
  isXxl: boolean,
): keyof BreakpointSize => {
  if (isXs) return 'xs';
  if (isXxl) return 'xxl';
  if (isXl) return 'xl';
  if (isLg) return 'lg';
  if (isMd) return 'md';
  if (isSm) return 'sm';
  return 'xs';
};

/* ************** CONTEXT ******************* */

const BreakpointContext = createContext<BreakpointContextValue | undefined>(undefined);

/* ************** PROVIDER ******************* */

export function BreakpointProvider({ children }: BreakpointProviderProps): JSX.Element {
  const isXs: boolean = useMediaQuery({ minWidth: BREAKPOINTS.xs, maxWidth: BREAKPOINTS.sm - 1 });
  const isSm: boolean = useMediaQuery({ minWidth: BREAKPOINTS.sm, maxWidth: BREAKPOINTS.md - 1 });
  const isMd: boolean = useMediaQuery({ minWidth: BREAKPOINTS.md, maxWidth: BREAKPOINTS.lg - 1 });
  const isLg: boolean = useMediaQuery({ minWidth: BREAKPOINTS.lg, maxWidth: BREAKPOINTS.xl - 1 });
  const isXl: boolean = useMediaQuery({ minWidth: BREAKPOINTS.xl, maxWidth: BREAKPOINTS.xxl - 1 });
  const isXxl: boolean = useMediaQuery({ minWidth: BREAKPOINTS.xxl });

  // Orientation detection
  const isPortrait: boolean = useMediaQuery({ orientation: 'portrait' });
  const isLandscape: boolean = useMediaQuery({ orientation: 'landscape' });

  const value: BreakpointContextValue = useMemo(() => {
    const currentBreakpoint: keyof BreakpointSize = calculateCurrentBreakpoint(
      isXs,
      isSm,
      isMd,
      isLg,
      isXl,
      isXxl,
    );

    return {
      isXs,
      isSm,
      isMd,
      isLg,
      isXl,
      isXxl,
      isPortrait,
      isLandscape,
      currentBreakpoint,
    };
  }, [
    isXs,
    isSm,
    isMd,
    isLg,
    isXl,
    isXxl,
    isPortrait,
    isLandscape,
  ]);

  return (
    <BreakpointContext.Provider value={value}>
      {children}
    </BreakpointContext.Provider>
  );
}

export function useBreakpoint(): BreakpointContextValue {
  const context = useContext<BreakpointContextValue | undefined>(BreakpointContext);

  if (!context) {
    throw new Error('useBreakpoint must be used within a BreakpointProvider');
  }

  return context;
}
